import React from "react";

import Richtext from "@dip/helsecore/src/components/shared/Wrappers/Richtext";
import SideMenu from "/react4xp/shared/SideMenu/Menu/SideMenu";
import { Link } from "@hdir/ui/link";
import { FontAwesomeIconWrapper } from "@dip/helsecore/src/components/shared/Wrappers/FontAwesomeIconWrapper";
import { faArrowLeft, faArrowRight, type IconDefinition } from "@fortawesome/pro-light-svg-icons";
import { TableOfContent } from "@dip/helsecore/src/components/parts/TableOfContent/TableOfContent";
import { RelatedLinks } from "@dip/helsecore/src/components/parts/RelatedLinks/RelatedLinks";
import { Alert } from "@hdir/ui/alert";
import PageHero from "/react4xp/shared/PageHero/PageHero";

import type { LinkItem, ShowKapittelProps } from "./type.d";

type NavLinkProps = {
  link?: LinkItem
  icon: IconDefinition;
  label: string;
  isNextLink?: boolean;
}

function NavLink({
  link, icon, label, isNextLink
}: NavLinkProps) {
  return link && (
    <Link className={`h-[99px] basis-52 shrink min-w-0 overflow-hidden ${isNextLink ? " ml-auto" : ""}`} variant="card" href={link.link}>
      <div className={`flex h-full w-full gap-xs ${isNextLink ? "flex-row-reverse" : "flex-row"} items-start min-w-0`}>
        <FontAwesomeIconWrapper className="shrink-0 self-center text-action" size="lg" icon={icon} />
        <div className="min-w-0 flex-1">
          <span className="text-action font-bold text-sm">{label}</span>
          <span className="line-clamp-2 overflow-ellipsis">{link.title}</span>
        </div>
      </div>
    </Link>
  );
}

export default function ShowKapittel(props: Readonly<ShowKapittelProps>) {
  const {
    alertText,
    title,
    sideMenuContent,
    currentUrl,
    text,
    prevLink,
    nextLink,
    preface,
    childrenLinkItems,
    chapterNumber,
    topNodeContent
  } = props;

  return (
    <div>
      { topNodeContent?.title
        && (
          <div className="my-xxl">
            <PageHero title={topNodeContent.title} headingLevel="p" image={topNodeContent?.image} chipText={topNodeContent?.productType} />
          </div>
        )}
      { alertText && (
        <Alert className="my-xl" fullWidth>
          <Richtext className="whitespace-normal" text={alertText} />
        </Alert>
      )}
      <div className="flex flex-col tablet:flex-row gap-xxxl">
        {(sideMenuContent && sideMenuContent?.length > 0) && (
          <aside className="hidden tablet:block basis-1/3 h-fit l-hide-to-lg">
            <SideMenu
              data={sideMenuContent}
              activeKey={currentUrl}
              title={topNodeContent?.title ? `Kapittelnavigasjon for ${topNodeContent.title}` : "Kapittelnavigasjon"}
              useHierarchicalNumbering
            />
          </aside>
        )}
        <div className="flex flex-col basis-2/3" id="article">
          { chapterNumber && (
            <p className="text-xl font-semibold mb-xs">
              {`Kapittel ${chapterNumber}`}
            </p>
          )}
          { title && <h1>{title}</h1> }
          <TableOfContent title="Innhold på siden" isClosed={false} isEditMode={false} />
          { preface && <Richtext className="t-body-text" text={preface} />}
          { text && <Richtext className="t-body-text" text={text} />}
          { childrenLinkItems?.length > 0 && (
            <RelatedLinks
              tittel=""
              columns="one"
              relatedLinks={childrenLinkItems}
              ariaLabel=""
              iconPath={null}
            />
          )}
          { (prevLink || nextLink) && (
            <div className="flex flex-row flex-nowrap justify-between gap-sm pt-md pb-xl">
              <NavLink link={prevLink} icon={faArrowLeft} label="Forrige" />
              <NavLink link={nextLink} icon={faArrowRight} label="Neste" isNextLink />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
